import type { V2Balance, V2XStock } from '../types/v2';

/** Account type holding one xStock (backend migration 00124). */
export const TOKENIZED_STOCK_ACCOUNT = 'tokenized_stock';

/**
 * Whether the balance sits on an xStocks account. Shares are held there, one
 * account per stock; USDT for buying them stays on the current account.
 */
export const isStockAccountBalance = (b: Pick<V2Balance, 'account_type_code'>): boolean =>
  b.account_type_code === TOKENIZED_STOCK_ACCOUNT;

// Display rules for xStocks (tokenized stocks). The backend symbol carries
// Kraken's "x" suffix ("AMZNx"); the design shows the plain ticker ("AMZN").

/** "AMZNx" → "AMZN". Symbols without the suffix are returned unchanged. */
export const xStockTicker = (symbol: string): string =>
  symbol.length > 1 && symbol.endsWith('x') ? symbol.slice(0, -1) : symbol;

/**
 * Ticker to show for any token: plain ticker for xStocks, the symbol itself
 * for everything else. xStockSymbols holds the exact xStock token symbols.
 */
export const displayTicker = (symbol: string, xStockSymbols: ReadonlySet<string>): string =>
  xStockSymbols.has(symbol) ? xStockTicker(symbol) : symbol;

/** "+2,69%" / "−1,20%"; empty when the change is unknown. */
export const formatPercentChange = (raw: string | number | null | undefined): string => {
  if (raw == null || raw === '') { return ''; }
  const value = Number(raw);
  if (!Number.isFinite(value)) { return ''; }
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(2).replace('.', ',')}%`;
};

/**
 * Why the stock cannot be traded, or null when it can. The backend enforces
 * the same rule; this only explains it up front. Price freshness plays no
 * part: the client sets the order price and the market rate is for
 * reference only (Exchange spec 3.3).
 */
export const tradingPausedReason = (stock: V2XStock): string | null => {
  if (stock.tradable) { return null; }
  if (!stock.token_id) { return 'This stock is not available for trading yet.'; }
  return 'This stock has no market pair and cannot be traded.';
};

/**
 * Note under the price when it is not live: "Price updated 25 min ago",
 * "No market price yet". Null while the price is live.
 */
export const priceAgeNote = (
  stock: Pick<V2XStock, 'price_live' | 'price_updated_at' | 'price_usdt'>,
  now: number = Date.now(),
): string | null => {
  if (stock.price_live) { return null; }
  const updated = stock.price_updated_at ? new Date(stock.price_updated_at).getTime() : NaN;
  if (!stock.price_usdt || !Number.isFinite(updated)) { return 'No market price yet'; }
  const minutes = Math.max(1, Math.floor((now - updated) / 60000));
  if (minutes < 60) { return `Price updated ${minutes} min ago`; }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) { return `Price updated ${hours} h ago`; }
  return `Price updated ${Math.floor(hours / 24)} d ago`;
};
